import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuizLayout } from '../components/QuizLayout';
import { PlanCard } from '../components/PlanCard';
import { LockedReveal } from '../components/LockedReveal';
import { RadarChart } from '../components/RadarChart';
import { PLANS, PLAN_FEATURES, type PlanId } from '../data/plans';
import { REVIEWS, REVIEW_STATS } from '../data/reviews';
import { useQuizStore } from '../store/quizStore';
import { useReportBySession } from '../lib/useReportBySession';
import { track } from '../lib/analytics';
import { firstNameOrNull } from '../lib/names';
import { audienceGroup, detectAudience } from '../data/audience';
import type { Report } from '../dashboard/reportTypes';
import { PricingContentB } from './PricingB';

// ── A/B variant assignment ───────────────────────────────────
// Assign each visitor to 'a' (current data-rich page) or 'b' (shorter
// marketing page). The B traffic share is server-configured (admin
// Experiments tab → /api/pricing-variant-config), so we can dial B from 0%
// (off) to 100% without a deploy. Assignment is sticky per browser
// (localStorage) for in-between splits; the 0%/100% extremes override the
// stored value so flipping the experiment off routes EVERYONE back to control
// immediately — including anyone previously bucketed to B.
type PricingVariant = 'a' | 'b';
const VARIANT_KEY = 'jobtest-pricing-variant';
const FORCE_KEY = 'jobtest-pricing-variant-force';

async function resolvePricingVariant(): Promise<PricingVariant> {
  if (typeof window === 'undefined') return 'a';

  // 0) Manual QA/preview override: ?variant=a|b (or ?pv=a|b). Authoritative —
  // bypasses the configured split (even 0%/100%) and sticks for the tab
  // session so it survives pricing → checkout navigation. Close the tab or
  // pass ?variant=a to clear.
  try {
    const params = new URLSearchParams(window.location.search);
    const param = params.get('variant') ?? params.get('pv');
    if (param === 'a' || param === 'b') {
      window.sessionStorage.setItem(FORCE_KEY, param);
      return param;
    }
    const forced = window.sessionStorage.getItem(FORCE_KEY);
    if (forced === 'a' || forced === 'b') return forced;
  } catch {
    /* noop */
  }

  // 1) Fetch the configured B share. Any failure / timeout → 100 (B is the
  //    live page). Bounded by an AbortController so a stalled request can't
  //    hang the variant gate (and thus the whole pricing render).
  let bPct = 100;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch('/api/pricing-variant-config', { signal: ctrl.signal });
    clearTimeout(timer);
    if (res.ok) {
      const json = (await res.json()) as { bPct?: unknown };
      const n = typeof json.bPct === 'number' ? json.bPct : Number(json.bPct);
      if (Number.isFinite(n)) bPct = Math.max(0, Math.min(100, n));
    }
  } catch {
    /* failure/timeout → keep bPct = 100 (B) */
  }

  // 2) Extremes override any stored assignment (instant on/off).
  if (bPct <= 0) return 'a';
  if (bPct >= 100) return 'b';

  // 3) In-between: sticky per browser, else roll the dice once.
  let variant: PricingVariant;
  try {
    const stored = window.localStorage.getItem(VARIANT_KEY);
    if (stored === 'a' || stored === 'b') {
      variant = stored;
    } else {
      variant = Math.random() * 100 < bPct ? 'b' : 'a';
      window.localStorage.setItem(VARIANT_KEY, variant);
    }
  } catch {
    variant = Math.random() * 100 < bPct ? 'b' : 'a';
  }

  // Surface to GTM so all subsequent dataLayer events can be segmented.
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'pricing_variant_assigned', pricing_variant: variant });
  } catch {
    /* noop */
  }
  return variant;
}
import {
  CompassIcon,
  SparkleIcon,
  TargetIcon,
  MicrophoneIcon,
  PinIcon,
  BriefcaseIcon,
  FlameIcon,
  ChipIcon,
  AwardIcon,
  ScalesIcon,
  StarFilledIcon,
  GroupIcon,
  ShieldIcon,
} from '../components/Icons';
import { HERO_IMAGE } from '../data/heroImages';
import { rankReviewsByPersona } from '../lib/reviewRanking';
import { COACHES } from '../data/coaches';

const fmtMoney = (n: number): string => '$' + n.toLocaleString('en-US');

const scrollToPricing = (e?: React.MouseEvent) => {
  e?.preventDefault();
  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export function Pricing() {
  const navigate = useNavigate();
  const setPlan = useQuizStore((s) => s.setPlan);
  const variables = useQuizStore((s) => s.variables);
  const leadName = useQuizStore((s) => s.leadName);
  const answers = useQuizStore((s) => s.answers);
  const quizStartedAt = useQuizStore((s) => s.quizStartedAt);
  const quizCompletedAt = useQuizStore((s) => s.quizCompletedAt);
  const { report } = useReportBySession();
  // Variant starts UNRESOLVED (null). We render a neutral canvas until the
  // server-configured split resolves, then render the chosen variant. This
  // eliminates the "flash of control" — we never paint the wrong variant
  // first (which happened when the initial state was hardcoded to 'a').
  const [variant, setVariant] = useState<PricingVariant | null>(null);

  useEffect(() => {
    let alive = true;
    void resolvePricingVariant().then((v) => {
      if (!alive) return;
      setVariant(v);
      track('pricing_viewed', { pricing_variant: v });
    });
    return () => {
      alive = false;
    };
  }, []);

  // `?wb=1` is appended to the deep link in abandoned-cart recovery emails.
  // It survives session hydration (only `sid` is stripped), so reading it on
  // mount is reliable. Drives the "welcome back" resume banner.
  const welcomeBack =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('wb') === '1';

  useEffect(() => {
    if (welcomeBack) track('pricing_welcome_back');
  }, [welcomeBack]);

  const handleSelect = (id: PlanId) => {
    setPlan(id);
    track('plan_selected', { plan: id, pricing_variant: variant });
    navigate(`/checkout/${id}`);
  };

  const sharedProps = {
    report,
    variables,
    answers,
    leadName,
    quizStartedAt,
    quizCompletedAt,
    onSelect: handleSelect,
    welcomeBack,
  };

  // Hold the render until the variant is known — a brief neutral canvas
  // beats flashing the wrong page. Resolution is fast (cached config fetch),
  // and the report itself is still loading async anyway.
  if (variant === null) {
    return <div className="min-h-dvh bg-canvas" aria-hidden />;
  }

  return variant === 'b' ? (
    <PricingContentB {...sharedProps} />
  ) : (
    <PricingContent {...sharedProps} />
  );
}

/**
 * Pure presentational pricing page. Takes everything it needs as props so
 * /dev/stub-preview (and any other surface) can render it with synthetic
 * data without touching the Zustand store or the report fetch hook.
 */
export function PricingContent({
  report,
  variables,
  answers,
  leadName,
  quizStartedAt,
  quizCompletedAt,
  onSelect,
  welcomeBack = false,
}: {
  report: Report | null;
  variables: Record<string, string>;
  answers: Record<string, { optionIds: string[]; freeText?: string; ts: number }>;
  leadName: string | null;
  quizStartedAt: number | null;
  quizCompletedAt: number | null;
  onSelect: (id: PlanId) => void;
  /** True when the visitor arrived from an abandoned-cart email (?wb=1). */
  welcomeBack?: boolean;
}) {
  const earningsCeiling = computeEarningsCeiling(report?.careers?.[0]?.salarySenior, variables);

  // Use the lead name only — never derive a "name" from the email. The old
  // fallback rendered the user's email as a greeting when (a) leadName was
  // empty, OR (b) they typed an email into the name field (since the regex
  // `split(/\s+/)[0]` returned the whole email unchanged). firstNameOrNull
  // rejects '@'-shaped inputs so neither case leaks to the page.
  const firstName = firstNameOrNull(leadName);

  return (
    <QuizLayout showBack={false} wide>
      <div className="space-y-14">
        {welcomeBack && <WelcomeBackBanner firstName={firstName} report={report} />}
        <PersonalizedHero firstName={firstName} report={report} />
        <SalaryDelta variables={variables} report={report} ceiling={earningsCeiling} />
        <LockedCareers report={report} />
        <YouToldUs variables={variables} answers={answers} />
        <RadarPreview report={report} />
        <StrengthsPreview report={report} />
        <SkillPercentiles report={report} />
        <WhyThisMatters variables={variables} firstName={firstName} />
        <ActionPlanTeaser report={report} />
        <TimeInvestment
          questionCount={Object.keys(answers).length}
          startedAt={quizStartedAt}
          completedAt={quizCompletedAt}
        />
        <TrustStrip />
        <ValuePillars />
        <PersonaReviews
          variables={variables}
          take={6}
          skip={0}
          eyebrow="Real results — from people like you"
          headline="850,000 people found direction. Your turn is now."
        />
        <MeetYourCoaches />
        <WeeklyCoaching />
        <PricingSection onSelect={onSelect} />
        <Guarantee />
        <PersonaReviews
          variables={variables}
          take={12}
          skip={6}
          eyebrow="More 5-star reviews"
          headline="Still on the fence? Here's what others said."
          showStats={false}
        />
      </div>
    </QuizLayout>
  );
}

// ============================================================
// 0. Bookmark / share link — small banner above the hero so a visitor
//    who came from one device + one URL can save their personalized
//    /pricing view and return on another. The sid never expires; the
//    link works across devices.
//
//    Rendered on /pricing only; the rest of the funnel mints the same
//    URL implicitly via localStorage. We strip `?sid=` from URLs on
//    landing (sessionHydration.ts) so the URL bar never shows it after
//    hydration — this banner is the only surface that exposes it.
// ============================================================


// ============================================================
// 0. Welcome-back banner (abandoned-cart return)
// ============================================================

// Shown when a visitor returns via an abandoned-cart recovery email (?wb=1).
// Resumes their context and reinforces what's still waiting behind the unlock.
function WelcomeBackBanner({
  firstName,
  report,
}: {
  firstName: string | null;
  report: Report | null;
}) {
  const topCareer = report?.careers?.[0]?.title ?? null;
  const typeName = report?.personality?.typeName ?? null;
  const detail =
    topCareer && typeName
      ? `Your #1 match (${topCareer}) and your full ${typeName} breakdown are still saved.`
      : topCareer
        ? `Your #1 match (${topCareer}) and your full report are still saved.`
        : 'Your personalized report is still saved and ready to unlock.';
  return (
    <div className="rounded-3xl border-2 border-teal-600/40 bg-teal-50 px-5 py-4 sm:px-6 sm:py-5 flex items-start gap-3">
      <span className="flex-shrink-0 mt-0.5 inline-flex w-8 h-8 rounded-full bg-teal-600 text-white items-center justify-center text-sm font-extrabold">
        ✓
      </span>
      <div>
        <p className="text-base sm:text-lg font-extrabold text-ink leading-tight">
          {firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}
        </p>
        <p className="text-sm text-ink-soft leading-relaxed mt-0.5">
          {detail} Pick a plan below to unlock everything.
        </p>
      </div>
    </div>
  );
}

// ============================================================
// 1. Personalized hero
// ============================================================

function PersonalizedHero({
  firstName,
  report,
}: {
  firstName: string | null;
  report: Report | null;
}) {
  const personality = report?.personality;
  const greeting = firstName ? `Hey ${firstName},` : 'Your report is ready.';
  return (
    <section className="space-y-10">
      {/* Top row: text left, BIG image right (top-aligned) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-6 lg:pt-2">
          <p className="eyebrow mb-3">Your report is ready</p>
          <h1 className="text-display-lg text-ink mb-4 leading-[1.05] text-balance">
            {personality ? (
              <>
                {greeting} you're a{' '}
                <span className="text-brand whitespace-nowrap">{personality.typeName}.</span>
              </>
            ) : (
              <>{greeting} your career report is ready.</>
            )}
          </h1>
          {personality && (
            <p className="inline-block rounded-full bg-ink text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 mb-6">
              Personality type · {personality.code}
            </p>
          )}
          <p className="text-ink-soft text-lg sm:text-xl mb-7 max-w-xl">
            That's the headline.{' '}
            <span className="font-bold text-ink">Your full report has the plan.</span>
          </p>
          <a
            href="#pricing"
            className="btn-primary !w-auto !inline-flex"
            onClick={(e) => {
              scrollToPricing(e);
              track('hero_cta_clicked');
            }}
          >
            Unlock my full report →
          </a>
        </div>

        {/* Image — bigger (square aspect, full column height) and top-aligned. */}
        <div className="lg:col-span-6 relative">
          <div className="aspect-[5/4] sm:aspect-[4/3] lg:aspect-square rounded-3xl overflow-hidden bg-canvas-subtle border-2 border-ink/10">
            <img
              src={HERO_IMAGE.src}
              alt={HERO_IMAGE.alt}
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border-2 border-ink/10 shadow-cardLg px-5 py-4 hidden sm:block">
            <p className="text-2xl sm:text-3xl font-extrabold text-ink">850,000+</p>
            <p className="text-xs text-ink-soft">people found direction</p>
          </div>
          <div className="absolute -top-6 -right-6 bg-brand rounded-2xl border-2 border-ink shadow-cta px-5 py-4 hidden sm:block">
            <p className="text-xs font-bold text-ink uppercase tracking-wider">5★ rated</p>
            <p className="text-2xl sm:text-3xl font-extrabold text-ink">{REVIEW_STATS.averageRating} / 5</p>
          </div>
        </div>
      </div>

      {/* Full-width "what unlocks" panel below the image */}
      <div className="rounded-3xl bg-canvas-tile border-2 border-ink/10 p-6 sm:p-10">
        <div className="mb-5">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink-soft">
            What unlocks when you upgrade ↓
          </p>
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
          {[
            <>
              <span className="font-bold text-ink">10 career matches</span> built for{' '}
              {personality ? <>{personality.typeName}s</> : <>you</>} — entry, mid, and senior
              salary tiers for each
            </>,
            <>
              <span className="font-bold text-ink">Your full personality breakdown</span>{' '}
              across 6 dimensions vs. your peer group
            </>,
            <>
              <span className="font-bold text-ink">3 core strengths + 3 growth areas</span>,
              each with specific actions to take next
            </>,
            <>
              <span className="font-bold text-ink">A 5-step 90-day plan</span> built from
              your actual answers, not a template
            </>,
            <>
              <span className="font-bold text-ink">Weekly live group coaching</span> —
              résumé reviews, salary negotiation, interview prep
            </>,
            <>
              <span className="font-bold text-ink">Resume & LinkedIn rewrite tools</span>{' '}
              tuned to your top career matches
            </>,
          ].map((node, i) => (
            <li key={i} className="flex items-start gap-3 text-base text-ink leading-snug">
              <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span>{node}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ============================================================
// 2. Salary delta
// ============================================================

function SalaryDelta({
  variables,
  report,
  ceiling,
}: {
  variables: Record<string, string>;
  report: Report | null;
  ceiling: number | null;
}) {
  const currentSalary = parseSalary(variables.currentSalary);
  const topMatch = report?.careers?.[0];
  if (!topMatch) return null;

  const audience = audienceGroup(detectAudience(variables));
  // For students/grads/returners, reframe the salary panel as "starting →
  // potential" because they may have $0 current salary (or none at all).
  if (audience === 'student' || audience === 'grad' || audience === 'returning') {
    const entry = topMatch.salaryEntry ?? null;
    const senior = topMatch.salarySenior ?? null;
    if (!entry && !senior) return null;
    const headlineLabel =
      audience === 'student' ? "Your career's starting potential"
      : audience === 'grad' ? 'Your first-job earning range'
      : 'Where you can land after returning';

    return (
      <section className="rounded-3xl bg-ink text-white px-6 sm:px-10 py-10 sm:py-12">
        <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-5">
          {headlineLabel}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-3 items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
              Entry-level
            </p>
            <p className="text-3xl sm:text-4xl font-extrabold tabular-nums">
              {entry ? fmtMoney(entry) : '—'}
              <span className="text-base font-normal text-white/60"> / yr</span>
            </p>
            <p className="text-sm text-white/60 mt-1">
              Where you'd start
            </p>
          </div>
          <div className="border-l-0 sm:border-l border-white/15 sm:pl-6">
            <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
              Mid-career
            </p>
            <p className="text-3xl sm:text-4xl font-extrabold tabular-nums text-brand">
              {topMatch.salaryMid ? fmtMoney(topMatch.salaryMid) : '—'}
              <span className="text-base font-normal text-white/60"> / yr</span>
            </p>
            <p className="text-sm text-white/60 mt-1">By year 4–6</p>
          </div>
          <div className="sm:border-l border-white/15 sm:pl-6">
            <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
              Senior-tier
            </p>
            <p className="text-3xl sm:text-4xl font-extrabold tabular-nums text-teal-300">
              {senior ? fmtMoney(senior) : '—'}
              <span className="text-base font-normal text-white/60"> / yr</span>
            </p>
            <p className="text-sm text-white/60 mt-1">By year 8+</p>
          </div>
        </div>
        <p className="text-sm text-white/70 mt-6 max-w-xl">
          Top match: <span className="font-bold text-white">{topMatch.title}</span>.
          Your career compounds — the early moves matter the most.
        </p>
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="inline-flex items-center gap-1.5 text-brand hover:text-brand-300 font-semibold text-sm mt-4 transition"
        >
          See all your career matches with full salary tiers <span aria-hidden>→</span>
        </a>
      </section>
    );
  }

  // Professional default: current vs senior with delta.
  const target = ceiling ?? topMatch.salarySenior ?? null;
  if (!target) return null;
  const delta = currentSalary && target ? target - currentSalary : null;

  return (
    <section className="rounded-3xl bg-ink text-white px-6 sm:px-10 py-10 sm:py-12">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-3 items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">Today</p>
          <p className="text-3xl sm:text-4xl font-extrabold tabular-nums">
            {currentSalary ? fmtMoney(currentSalary) : '—'}
            <span className="text-base font-normal text-white/60"> / yr</span>
          </p>
          <p className="text-sm text-white/60 mt-1">
            {currentSalary ? 'Your current salary' : "You didn't say"}
          </p>
        </div>
        <div className="border-l-0 sm:border-l border-white/15 sm:pl-6">
          <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
            Top match's senior tier
          </p>
          <p className="text-3xl sm:text-4xl font-extrabold tabular-nums text-brand">
            {fmtMoney(target)}
            <span className="text-base font-normal text-white/60"> / yr</span>
          </p>
          <p className="text-sm text-white/60 mt-1">
            {topMatch.title ?? 'Your strongest match'}
          </p>
        </div>
        <div className="sm:border-l border-white/15 sm:pl-6">
          <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
            Potential delta
          </p>
          <p className="text-3xl sm:text-4xl font-extrabold tabular-nums text-teal-300">
            {delta != null && delta > 0 ? `+${fmtMoney(delta)}` : '—'}
            <span className="text-base font-normal text-white/60"> / yr</span>
          </p>
          <p className="text-sm text-white/60 mt-1">
            {delta != null && delta > 0 ? 'Upside on your top match' : 'Add salary in dashboard'}
          </p>
        </div>
      </div>
      <a
        href="#pricing"
        onClick={scrollToPricing}
        className="inline-flex items-center gap-1.5 text-brand hover:text-brand-300 font-semibold text-sm mt-6 transition"
      >
        See all 10 career matches with full salary tiers <span aria-hidden>→</span>
      </a>
    </section>
  );
}

// ============================================================
// 3. Locked top-3 careers
// ============================================================

function LockedCareers({ report }: { report: Report | null }) {
  if (!report?.careers?.length) return null;
  const top3 = report.careers.slice(0, 3);
  const remaining = Math.max(0, report.careers.length - 3);

  return (
    <section>
      <header className="text-center mb-8">
        <p className="eyebrow mb-3">Your top 10 career matches</p>
        <h2 className="text-display-md text-ink mb-3">
          You matched with{' '}
          <span className="text-brand">{report.careers.length} careers</span>.
        </h2>
        <p className="text-ink-soft text-base sm:text-lg max-w-2xl mx-auto">
          Here's a peek at your top 3. The other {remaining} — plus full salary tiers, fit
          reasons, and growth paths for each — unlock with any plan.
        </p>
      </header>

      <ul className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {top3.map((c, i) => (
          <li key={c.id}>
            <div className="rounded-2xl border-2 border-ink/10 bg-white p-6 flex flex-col h-full">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">
                  Match #{i + 1}
                </span>
                <span className="rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-0.5">
                  {c.matchPercent}% match
                </span>
              </div>
              <h3 className="text-lg font-extrabold text-ink mb-3">{c.title}</h3>

              <LockedReveal unlockLabel="full match details" className="flex-1">
                <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b border-ink/10">
                  <Tier label="Entry" value={fmtMoney(c.salaryEntry)} />
                  <Tier label="Mid" value={fmtMoney(c.salaryMid)} />
                  <Tier label="Senior" value={fmtMoney(c.salarySenior)} />
                </div>
                <p className="text-sm text-ink-soft leading-relaxed mb-3">{c.description}</p>
                <p className="text-xs font-bold uppercase tracking-wider text-ink mb-2">
                  Why this fits you
                </p>
                <ul className="space-y-1.5">
                  {c.fitReasons.slice(0, 3).map((r) => (
                    <li key={r} className="text-xs text-ink-soft">• {r}</li>
                  ))}
                </ul>
              </LockedReveal>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-8 text-center">
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="btn-primary !w-auto !inline-flex"
        >
          Unlock all {report.careers.length} matches →
        </a>
        <p className="text-sm text-ink-soft mt-3">
          Each match includes salary tiers, fit reasons, and a learn-more deep-dive.
        </p>
      </div>
    </section>
  );
}

function Tier({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-base font-bold text-ink">{value}</p>
      <p className="text-xs text-ink-soft mt-0.5">{label}</p>
    </div>
  );
}

// ============================================================
// 4. "You told us..." recap
// ============================================================

function YouToldUs({
  variables,
  answers,
}: {
  variables: Record<string, string>;
  answers: Record<string, unknown>;
}) {
  const items = useMemo(() => buildRecapItems(variables), [variables]);
  if (items.length === 0) return null;
  const answeredCount = Object.keys(answers).length;

  return (
    <section className="rounded-3xl bg-canvas-tile border-2 border-ink/10 px-6 sm:px-10 py-10">
      <header className="text-center mb-8">
        <p className="eyebrow mb-3">We listened</p>
        <h2 className="text-display-md text-ink mb-3">You told us...</h2>
        <p className="text-ink-soft text-base">
          Your strategy is built around{answeredCount > 0 ? <> these {answeredCount} answers</> : ' your responses'}.
        </p>
      </header>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {items.map((it) => (
          <li
            key={it.label}
            className="flex items-start gap-3 rounded-2xl bg-white border-2 border-ink/10 px-5 py-4"
          >
            <span className="flex-shrink-0 w-9 h-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center">
              <it.Icon size={18} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-1">
                {it.label}
              </p>
              <p className="text-base font-semibold text-ink leading-snug">{it.value}</p>
            </div>
          </li>
        ))}
      </ul>
      <div className="text-center mt-7">
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="text-teal-700 hover:text-teal-800 font-semibold text-sm inline-flex items-center gap-1 transition"
        >
          Get the strategy built around these <span aria-hidden>→</span>
        </a>
      </div>
    </section>
  );
}

// ============================================================
// 5. Radar preview
// ============================================================

function RadarPreview({ report }: { report: Report | null }) {
  if (!report?.personality) return null;
  const { radar, peerRadar, dominantAxes } = report.personality;
  if (!radar || !peerRadar || !Array.isArray(dominantAxes)) return null;

  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <div>
        <p className="eyebrow mb-3">Your personality vs your peers</p>
        <h2 className="text-display-md text-ink mb-4">
          You scored above average on{' '}
          <span className="text-teal-700">{dominantAxes.join(' & ')}</span>.
        </h2>
        <p className="text-ink-soft text-base sm:text-lg mb-6">
          Your full report shows your score across all{' '}
          <span className="font-bold text-ink">6 dimensions</span> — Communal,
          Analytical, Pragmatic, Bold, Orthodox, and Imaginative — and what each one
          means for the kinds of work you'll thrive in.
        </p>
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="btn-primary !w-auto !inline-flex"
        >
          See all 6 dimensions →
        </a>
      </div>

      <div className="relative">
        <div className="rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-8 flex justify-center">
          <RadarChart
            user={radar}
            peer={peerRadar}
            highlight={dominantAxes.map((a) => a.toLowerCase())}
            size={320}
          />
        </div>
        <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-3 text-sm">
          <li className="flex items-center gap-2 text-ink">
            <span className="w-4 h-2 rounded-full bg-teal-400" /> You
          </li>
          <li className="flex items-center gap-2 text-ink-soft">
            <span className="w-4 h-2 rounded-full bg-red-300" /> Peer baseline
          </li>
        </ul>
      </div>
    </section>
  );
}

// ============================================================
// 6. Strengths preview
// ============================================================

function StrengthsPreview({ report }: { report: Report | null }) {
  if (!report?.strengths?.length) return null;
  const [first, ...rest] = report.strengths;
  const weaknessesLen = report.weaknesses?.length ?? 0;
  const workplaceLen = report.workplaceTraits?.length ?? 0;
  const lockedCount = rest.length + weaknessesLen + workplaceLen;

  return (
    <section>
      <header className="text-center mb-8">
        <p className="eyebrow mb-3">Your strengths</p>
        <h2 className="text-display-md text-ink mb-3">
          We named <span className="text-brand">{report.strengths.length} core strengths</span>{' '}
          and <span className="text-brand">{weaknessesLen} growth areas</span> for you.
        </h2>
        <p className="text-ink-soft text-base">
          Here's the first one. {lockedCount} more inside.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="rounded-2xl border-2 border-ink bg-brand-50 p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-700 mb-2">
            Strength #1
          </p>
          <h3 className="text-lg font-extrabold text-ink mb-3">{first.name}</h3>
          <p className="text-sm text-ink-soft leading-relaxed">{first.description}</p>
        </div>
        {rest.slice(0, 2).map((s, i) => (
          <div key={s.name} className="rounded-2xl border-2 border-ink/10 bg-white">
            <LockedReveal unlockLabel={`strength #${i + 2}`} blur="md">
              <div className="p-6">
                <p className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-2">
                  Strength #{i + 2}
                </p>
                <h3 className="text-lg font-extrabold text-ink mb-3">{s.name}</h3>
                <p className="text-sm text-ink-soft leading-relaxed">{s.description}</p>
              </div>
            </LockedReveal>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// 7. Skill percentiles
// ============================================================

function SkillPercentiles({ report }: { report: Report | null }) {
  if (!report?.personality?.radar || !report.personality.peerRadar) return null;
  const items: { label: string; topPercent: number }[] = [];
  for (const [axis, label] of [
    ['analytical', 'Analytical strength'],
    ['pragmatic', 'Pragmatic execution'],
    ['communal', 'People orientation'],
    ['bold', 'Risk tolerance'],
    ['imaginative', 'Creative thinking'],
    ['orthodox', 'Structured approach'],
  ] as const) {
    const u = report.personality.radar[axis];
    const p = report.personality.peerRadar[axis];
    if (typeof u !== 'number' || typeof p !== 'number') continue;
    const delta = u - p;
    const topPercent = Math.max(2, Math.min(70, Math.round(50 - delta)));
    if (delta > 5) items.push({ label, topPercent });
  }
  items.sort((a, b) => a.topPercent - b.topPercent);
  const visible = items.slice(0, 3);
  if (visible.length === 0) return null;

  return (
    <section>
      <header className="text-center mb-8">
        <p className="eyebrow mb-3">How you stack up</p>
        <h2 className="text-display-md text-ink mb-3">
          You're in the <span className="text-teal-700">top {visible[0].topPercent}%</span> for{' '}
          <span className="text-ink">{visible[0].label.toLowerCase()}</span>.
        </h2>
        <p className="text-ink-soft text-base">
          Your full report ranks you across <span className="font-bold text-ink">12+ skills</span>.
        </p>
      </header>

      <ul className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
        {visible.map((it, i) => (
          <li key={it.label} className="rounded-2xl border-2 border-ink/10 bg-white p-6 text-center">
            <p className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-3">
              Skill #{i + 1}
            </p>
            <p className="text-display-sm text-teal-700 font-extrabold mb-1">
              Top {it.topPercent}%
            </p>
            <p className="text-base font-semibold text-ink">{it.label}</p>
          </li>
        ))}
      </ul>

      <div className="text-center mt-7">
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="text-teal-700 hover:text-teal-800 font-semibold text-sm inline-flex items-center gap-1 transition"
        >
          See all 12+ skill rankings <span aria-hidden>→</span>
        </a>
      </div>
    </section>
  );
}

// ============================================================
// 8. Why this matters now
// ============================================================

function WhyThisMatters({
  variables,
  firstName,
}: {
  variables: Record<string, string>;
  firstName: string | null;
}) {
  const concern = pickPrimaryConcern(variables);
  if (!concern) return null;
  const name = firstName ?? 'You';

  return (
    <section className="rounded-3xl bg-gradient-to-br from-brand-50 to-canvas-tile border-2 border-ink/10 px-6 sm:px-10 py-10">
      <div className="max-w-3xl mx-auto text-center">
        <p className="eyebrow mb-3">Why this matters now</p>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-ink mb-4">
          {concern.headline(name)}
        </h2>
        <p className="text-ink-soft text-base sm:text-lg leading-relaxed mb-6">
          {concern.body}
        </p>
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="btn-primary !w-auto !inline-flex"
        >
          {concern.cta} →
        </a>
      </div>
    </section>
  );
}

// ============================================================
// 9. 90-day plan teaser
// ============================================================

function ActionPlanTeaser({ report }: { report: Report | null }) {
  // Flatten all strategy tasks into one ordered list — first task is shown
  // in full, rest are blurred. Matches the "Step 1 is on us" framing.
  const phases = report?.strategy?.phases ?? [];
  const flat = phases.flatMap((p) =>
    (p.tasks ?? []).map((t) => ({ step: t.t, detail: '', timeframe: p.label })),
  );
  if (!flat.length) return null;
  const teaserItems = flat.slice(0, 5);
  const [first, ...rest] = teaserItems;

  return (
    <section>
      <header className="text-center mb-8">
        <p className="eyebrow mb-3">Your 90-day plan</p>
        <h2 className="text-display-md text-ink mb-3">
          A 5-step plan, built from your answers.
        </h2>
        <p className="text-ink-soft text-base">Step 1 is on us. The next 4 are inside.</p>
      </header>

      <ol className="space-y-3 max-w-3xl mx-auto">
        <li className="flex items-start gap-4 rounded-2xl border-2 border-ink bg-brand-50 p-5">
          <span className="flex-shrink-0 w-9 h-9 rounded-full bg-ink text-white text-sm font-bold flex items-center justify-center">
            1
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base font-extrabold text-ink">{first.step}</h3>
              <span className="text-xs font-bold uppercase tracking-wider text-brand-700 flex-shrink-0">
                {first.timeframe}
              </span>
            </div>
          </div>
        </li>

        {rest.map((s, i) => (
          <li key={`${s.step}-${i}`}>
            <LockedReveal unlockLabel={`step ${i + 2}`} blur="sm">
              <div className="flex items-start gap-4 rounded-2xl border-2 border-ink/10 bg-white p-5">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-canvas-subtle text-ink-soft text-sm font-bold flex items-center justify-center">
                  {i + 2}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-base font-extrabold text-ink">{s.step}</h3>
                    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft flex-shrink-0">
                      {s.timeframe}
                    </span>
                  </div>
                </div>
              </div>
            </LockedReveal>
          </li>
        ))}
      </ol>

      <div className="text-center mt-7">
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="btn-primary !w-auto !inline-flex"
        >
          Unlock the full action plan →
        </a>
      </div>
    </section>
  );
}

// ============================================================
// 10. Time-investment validator
// ============================================================

function TimeInvestment({
  questionCount,
  startedAt,
  completedAt,
}: {
  questionCount: number;
  startedAt: number | null;
  completedAt: number | null;
}) {
  if (questionCount < 5) return null;
  const minutes =
    startedAt && completedAt
      ? Math.max(1, Math.round((completedAt - startedAt) / 60_000))
      : null;

  return (
    <section className="rounded-3xl border-2 border-ink/10 bg-white px-6 sm:px-10 py-10 text-center">
      <p className="eyebrow mb-3">You've already done the work</p>
      <p className="text-2xl sm:text-3xl font-extrabold text-ink mb-3">
        You answered <span className="text-brand">{questionCount} questions</span>
        {minutes ? (
          <>
            {' '}in{' '}
            <span className="text-brand">
              {minutes} minute{minutes === 1 ? '' : 's'}
            </span>
          </>
        ) : null}
        .
      </p>
      <p className="text-ink-soft text-base sm:text-lg leading-relaxed mb-5">
        That work isn't wasted — your full personalized report is sitting on the other side
        of this page. Don't lose your spot.
      </p>
      <a
        href="#pricing"
        onClick={scrollToPricing}
        className="btn-primary !w-auto !inline-flex"
      >
        Get your report →
      </a>
    </section>
  );
}

// ============================================================
// Trust strip / Value pillars / Reviews / Pricing / Guarantee
// ============================================================

function TrustStrip() {
  return (
    <section className="rounded-3xl bg-gradient-to-br from-brand-50 via-canvas-tile to-teal-50 border-2 border-ink/10 px-6 sm:px-10 py-9 overflow-hidden relative">
      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-3 text-center relative">
        <li className="flex flex-col items-center">
          <div className="inline-flex gap-1 mb-3 text-brand drop-shadow-sm">
            {[1, 2, 3, 4, 5].map((n) => (
              <StarFilledIcon key={n} size={32} />
            ))}
          </div>
          <p className="text-2xl sm:text-3xl font-extrabold text-ink mb-1 tabular-nums">
            <span className="text-brand">{REVIEW_STATS.averageRating}</span>
            <span className="text-ink-soft text-lg font-bold"> / 5</span>
          </p>
          <p className="text-sm font-semibold text-ink">
            {REVIEW_STATS.totalCount.toLocaleString()}+ reviews on Trustpilot
          </p>
        </li>
        <li className="flex flex-col items-center sm:border-l sm:border-r sm:border-ink/15 sm:px-2">
          <div className="w-14 h-14 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center mb-3">
            <GroupIcon size={28} />
          </div>
          <p className="text-3xl font-extrabold text-ink mb-1">850,000+</p>
          <p className="text-sm font-semibold text-ink">people found their next move</p>
        </li>
        <li className="flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center mb-3">
            <ShieldIcon size={28} />
          </div>
          <p className="text-3xl font-extrabold text-ink mb-1">100%</p>
          <p className="text-sm font-semibold text-ink">money-back guarantee</p>
        </li>
      </ul>
    </section>
  );
}

function ValuePillars() {
  const pillars: Array<{
    Icon: typeof CompassIcon;
    iconBg: string;
    iconColor: string;
    title: string;
    blurb: string;
  }> = [
    {
      Icon: CompassIcon,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-700',
      title: 'Explore career options',
      blurb: 'aligned with your unique aspirations and the way you actually work',
    },
    {
      Icon: SparkleIcon,
      iconBg: 'bg-brand-100',
      iconColor: 'text-brand-700',
      title: 'Understand your strengths',
      blurb: 'know what makes you valuable — and exactly how to leverage it',
    },
    {
      Icon: TargetIcon,
      iconBg: 'bg-canvas-tile',
      iconColor: 'text-ink',
      title: 'Take control of your path',
      blurb: 'move from "what now?" to a clear, actionable next 90 days',
    },
    {
      Icon: MicrophoneIcon,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-700',
      title: 'Personalized coaching support',
      blurb: 'live group sessions led by experienced career coaches, every week',
    },
  ];
  return (
    <section className="text-center">
      <p className="eyebrow mb-3">Why now</p>
      <h2 className="text-display-md text-ink mb-4">
        20 minutes today will change your next 20 years.
      </h2>
      <p className="text-ink-soft text-lg max-w-2xl mx-auto mb-10">
        Your subscription unlocks four things — the rest of your career runs on what you do
        with them.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 text-left">
        {pillars.map(({ Icon, iconBg, iconColor, title, blurb }) => (
          <div key={title} className="rounded-2xl border-2 border-ink/10 bg-white p-6">
            <div className={`w-12 h-12 rounded-2xl ${iconBg} ${iconColor} flex items-center justify-center mb-4`}>
              <Icon size={26} />
            </div>
            <h3 className="text-base font-bold text-ink mb-2">{title}</h3>
            <p className="text-sm text-ink-soft leading-relaxed">{blurb}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

interface ReviewSectionProps {
  variables: Record<string, string>;
  /** First N from the persona-ranked list. Use this OR `skip+take`. */
  take?: number;
  /** Skip the first N from the ranked list (use for the bottom section). */
  skip?: number;
  eyebrow: string;
  headline: string;
  showStats?: boolean;
}

function PersonaReviews({
  variables,
  take = 6,
  skip = 0,
  eyebrow,
  headline,
  showStats = true,
}: ReviewSectionProps) {
  const ranked = useMemo(() => rankReviewsByPersona(REVIEWS, variables), [variables]);
  const visible = ranked.slice(skip, skip + take);
  if (visible.length === 0) return null;
  return (
    <section>
      <div className="text-center mb-9">
        <p className="eyebrow mb-3">{eyebrow}</p>
        <h2 className="text-display-md text-ink mb-3">{headline}</h2>
        {showStats && (
          <div className="inline-flex items-center gap-2 mt-2">
            <Stars rating={REVIEW_STATS.averageRating} />
            <span className="text-base font-semibold text-ink">{REVIEW_STATS.averageRating}</span>
            <span className="text-sm text-ink-soft">
              · {REVIEW_STATS.totalCount.toLocaleString()}+ Trustpilot reviews
            </span>
          </div>
        )}
      </div>
      {/* Masonry-style flow via CSS columns. Each card sizes to its content
           (no equal-height stretch); break-inside-avoid keeps cards intact;
           mb-5 handles vertical spacing inside a column; gap-5 handles
           horizontal gap between columns. */}
      <ul className="columns-1 md:columns-2 lg:columns-3 gap-5">
        {visible.map((r) => (
          <li
            key={r.id}
            className="rounded-2xl border-2 border-ink/10 bg-white p-6 mb-5 break-inside-avoid"
          >
            <div className="flex items-center gap-2 mb-3">
              <Stars rating={r.rating} small />
              {r.verified && (
                <span className="ml-auto text-xs font-bold text-teal-700 inline-flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Verified
                </span>
              )}
            </div>
            <h3 className="text-base font-bold text-ink mb-2 leading-snug">{r.title}</h3>
            <p className="text-sm text-ink-soft leading-relaxed mb-4">{r.body}</p>
            <p className="text-xs text-ink-light">
              — {r.author},{' '}
              {new Date(r.date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Stars({ rating, small = false }: { rating: number; small?: boolean }) {
  const size = small ? 16 : 20;
  return (
    <span className="inline-flex gap-0.5 text-brand" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <StarFilledIcon
          key={n}
          size={size}
          style={n <= rating ? undefined : { color: '#f5f5f4' }}
        />
      ))}
    </span>
  );
}

function PricingSection({ onSelect }: { onSelect: (id: PlanId) => void }) {
  return (
    <section id="pricing">
      <div className="text-center mb-9">
        <p className="eyebrow mb-3">Pick your plan</p>
        <h2 className="text-display-md text-ink mb-3">Unlock your potential.</h2>
        <p className="text-ink-soft text-base sm:text-lg max-w-2xl mx-auto">
          Every plan includes the full report, weekly live coaching, and a 100%
          money-back guarantee. Pick the cadence that fits how seriously you want to make
          this change.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
        {PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            selected={false}
            onSelect={() => onSelect(plan.id)}
          />
        ))}
      </div>

      <div className="mt-10 rounded-3xl bg-canvas-subtle border-2 border-ink/10 p-6 sm:p-10">
        <h3 className="text-lg font-bold text-ink mb-5 text-center">Every plan includes:</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3.5 max-w-3xl mx-auto">
          {PLAN_FEATURES.map((f) => (
            <li key={f.label} className="flex items-start gap-3 text-sm text-ink">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0d9d7c"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                className="flex-shrink-0 mt-0.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>
                <span className="font-bold">{f.label}</span>
                {f.detail && (
                  <span className="block text-ink-soft text-xs mt-0.5">{f.detail}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Guarantee() {
  return (
    <section className="text-center max-w-2xl mx-auto pb-4">
      <div className="inline-flex items-center gap-2 mb-3">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#0d9d7c"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
        <p className="text-sm font-bold uppercase tracking-wider text-teal-700">
          100% money-back guarantee
        </p>
      </div>
      <p className="text-ink-soft text-base">
        If your report doesn't help you see your career more clearly within 30 days, we'll
        refund you in full. No forms, no friction.
      </p>
    </section>
  );
}

// ============================================================
// Meet your coaches — small humanizer above PricingSection
// ============================================================

function MeetYourCoaches() {
  return (
    <section className="max-w-4xl mx-auto">
      <header className="text-center mb-6">
        <p className="eyebrow mb-2">Meet your coaches</p>
        <h2 className="text-display-sm text-ink text-balance">Real humans, real outcomes.</h2>
      </header>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {COACHES.map((c) => (
          <CoachCard key={c.id} coach={c} />
        ))}
      </div>
    </section>
  );
}

function CoachCard({ coach }: { coach: typeof COACHES[number] }) {
  return (
    <article className="rounded-2xl border-2 border-ink/10 bg-white p-4 flex flex-col items-center text-center">
      <img
        src={coach.imageSrc}
        alt={coach.name}
        className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full object-cover border-2 border-ink/10 mb-3"
        loading="lazy"
        width={72}
        height={72}
      />
      <h3 className="text-sm font-extrabold text-ink leading-tight">{coach.name}</h3>
      <p className="text-xs text-ink-soft mb-2">{coach.title}</p>
      {coach.rating != null && coach.clientsServed && (
        <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-ink-soft">
          <span aria-hidden className="text-brand">★</span>
          <span className="tabular-nums text-ink">{coach.rating.toFixed(2)}</span>
          <span className="text-ink-light">·</span>
          <span className="tabular-nums">{coach.clientsServed}</span>
        </div>
      )}
    </article>
  );
}

// ============================================================
// Weekly coaching — what's actually included in the paid plan
// ============================================================

const COACHING_ITEMS: Array<{
  title: string;
  body: string;
  iconBg: string;
  icon: React.ReactNode;
}> = [
  {
    title: 'Weekly group coaching',
    body: 'Bring a problem, leave with a plan. Small-group calls every week.',
    iconBg: 'bg-brand-50 text-brand-700',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    title: 'Résumé & LinkedIn reviews',
    body: 'Get your résumé and LinkedIn rewritten live on the call.',
    iconBg: 'bg-teal-50 text-teal-700',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
      </svg>
    ),
  },
  {
    title: 'Weekly job roundup',
    body: 'Hand-picked openings each week that match your top careers.',
    iconBg: 'bg-ink/5 text-ink',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
  },
  {
    title: 'Offer & salary clinic',
    body: 'Bring your offer. Leave with the script to negotiate it up.',
    iconBg: 'bg-brand-50 text-brand-700',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    title: 'Mock interview drills',
    body: 'Practice your top 5 answers with a coach and small peer group.',
    iconBg: 'bg-teal-50 text-teal-700',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    ),
  },
  {
    title: 'Application sprint',
    body: 'Send 5 tailored applications in 90 minutes, together with peers.',
    iconBg: 'bg-ink/5 text-ink',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
];

function WeeklyCoaching() {
  return (
    <section className="max-w-5xl mx-auto">
      {/* Distinct boxed treatment to set this section apart from the
          surrounding white-on-white sections. Warm canvas-tile background
          + 2px border + soft elevation + signature offset shadow. */}
      <div className="rounded-card border-2 border-ink/10 bg-canvas-tile p-4 sm:p-10 shadow-[6px_6px_0_0_rgba(15,18,23,1)]">
        {/* Hero: text-left + photo-right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center mb-8 sm:mb-10">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-3">What's included</p>
            <h2 className="text-display-md text-ink leading-tight mb-3 text-balance">
              4 hours of live coaching, every month.
            </h2>
            <p className="text-ink-soft text-base sm:text-lg max-w-xl">
              Show up live, ask questions, leave with one thing to try. Six session types — pick the ones that fit where you are.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden border-2 border-ink/10 bg-canvas-subtle">
              <picture>
                <source srcSet="/coaches/hero.webp" type="image/webp" />
                <img
                  src="/coaches/hero.png"
                  alt="A career coach on a live video call"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1600}
                  height={711}
                />
              </picture>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {COACHING_ITEMS.map((c) => (
            <article key={c.title} className="rounded-2xl border-2 border-ink/10 bg-white p-5 flex items-start gap-3">
              <span className={`flex-shrink-0 w-10 h-10 rounded-xl ${c.iconBg} flex items-center justify-center`}>
                {c.icon}
              </span>
              <div className="min-w-0">
                <h3 className="text-base font-extrabold text-ink leading-tight mb-1">{c.title}</h3>
                <p className="text-sm text-ink-soft leading-relaxed">{c.body}</p>
              </div>
            </article>
          ))}
        </div>

        <p className="text-center text-sm text-ink-soft mt-6">
          <span className="font-extrabold text-ink">99%</span> of members got an interview within 3 months.
        </p>
      </div>
    </section>
  );
}

// ============================================================
// Helpers
// ============================================================

const SKILL_VAR_TO_LABEL: Record<string, string> = {
  leadershipConfidenceScore: 'Leadership confidence',
  decisionMakingScore: 'Decision-making',
  communicationSkillsScore: 'Communication',
  strategicThinkingScore: 'Strategic thinking',
  attentionToDetailScore: 'Attention to detail',
  organizationalSkillsScore: 'Organization',
  teamManagementScore: 'Team management',
  timeManagementScore: 'Time management',
  presentationSkillsScore: 'Presentation skills',
  dataAnalysisScore: 'Data analysis',
  salesSkillsScore: 'Sales',
  changeAdaptationScore: 'Change adaptation',
  researchSkillsScore: 'Research',
  statisticalAnalysisScore: 'Statistical analysis',
  computerProficiencyScore: 'Computer proficiency',
};

function parseSalary(s: string | undefined): number | null {
  if (!s) return null;
  const digits = s.replace(/[^0-9]/g, '');
  if (!digits) return null;
  const n = parseInt(digits, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function computeEarningsCeiling(
  topCareerSenior: number | undefined,
  variables: Record<string, string>,
): number | null {
  if (topCareerSenior && topCareerSenior > 0) return topCareerSenior;
  const current = parseSalary(variables.currentSalary);
  if (current) {
    if (current < 50_000) return 100_000;
    if (current < 100_000) return 150_000;
    if (current < 150_000) return 220_000;
    if (current < 250_000) return 320_000;
    return current * 2;
  }
  return 200_000;
}

interface RecapItem {
  label: string;
  value: string;
  Icon: typeof TargetIcon;
}

function buildRecapItems(variables: Record<string, string>): RecapItem[] {
  const items: RecapItem[] = [];

  const purpose = variables.testTakingPurpose;
  if (purpose) {
    const map: Record<string, string> = {
      careerSwitch: "You're considering a career switch",
      lost: "You're feeling unsure about what's next",
      return: "You're returning to the workforce",
      advance: "You're aiming for advancement",
      collegeMajor: "You're exploring college majors",
      ai: "You're curious about AI's impact on your career",
      other: 'You came here to explore',
    };
    if (map[purpose]) items.push({ label: 'Why you came', value: map[purpose], Icon: TargetIcon });
  }

  const status = variables.employmentStatus;
  if (status) {
    const map: Record<string, string> = {
      fulltime: "You're employed full-time",
      parttime: "You're working part-time",
      searching: "You're actively looking for a new role",
      notSearching: "You're employed but open to options",
    };
    if (map[status]) items.push({ label: 'Where you are', value: map[status], Icon: PinIcon });
  }

  const job = variables.currentJob ?? variables.currentJobOther;
  if (job) items.push({ label: 'What you do today', value: job, Icon: BriefcaseIcon });

  if (variables.burnoutExperience === 'true') {
    items.push({ label: 'A challenge you named', value: "You've experienced burnout", Icon: FlameIcon });
  }

  if (variables.aiImpactConcern && Number(variables.aiImpactConcern) >= 4) {
    items.push({ label: 'On your mind', value: "AI's impact on your career", Icon: ChipIcon });
  }

  // Top score
  const scoreEntries = Object.entries(variables)
    .filter(([k]) => k.endsWith('Score'))
    .map(([k, v]) => [k, Number(v)] as const)
    .filter(([, n]) => Number.isFinite(n) && n >= 4);
  if (scoreEntries.length > 0) {
    scoreEntries.sort((a, b) => b[1] - a[1]);
    const [topKey] = scoreEntries[0];
    const label = SKILL_VAR_TO_LABEL[topKey] ?? topKey.replace(/Score$/, '');
    items.push({ label: 'Your strongest score', value: label, Icon: AwardIcon });
  }

  if (variables.balanceMeaning) {
    const map: Record<string, string> = {
      separation: 'Clear separation between work and personal time',
      flexibility: 'Flexibility to manage both work and personal commitments',
      morePersonal: 'Prioritizing personal life over work',
      integrated: 'Integrating work seamlessly into life',
    };
    if (map[variables.balanceMeaning]) {
      items.push({
        label: 'What balance means to you',
        value: map[variables.balanceMeaning],
        Icon: ScalesIcon,
      });
    }
  }

  return items.slice(0, 6);
}

function pickPrimaryConcern(variables: Record<string, string>): {
  headline: (name: string) => string;
  body: string;
  cta: string;
} | null {
  if (variables.burnoutExperience === 'true') {
    return {
      headline: (name) => `${name}, the burnout pattern repeats unless you change the inputs.`,
      body: "You said you've experienced burnout. Most people who've burnt out once will burn out again — unless they identify the specific triggers in their work and intentionally redesign around them. Your report names the trigger patterns in your answers and matches you with careers that minimize them.",
      cta: 'Get the burnout-aware match list',
    };
  }
  if (variables.aiImpactConcern && Number(variables.aiImpactConcern) >= 4) {
    return {
      headline: (name) => `${name}, AI is going to reshape your work — knowing how is the unlock.`,
      body: "You said you're worried about AI's impact on your career. The hard truth: every role on your match list will be touched by AI in the next 24 months. The soft truth: if you know which parts of YOUR strengths AI amplifies vs replaces, the next two years are an opportunity, not a threat. Your report names that specifically.",
      cta: 'See your AI-resilient matches',
    };
  }
  if (variables.testTakingPurpose === 'lost') {
    return {
      headline: (name) => `${name}, "lost" is information, not a verdict.`,
      body: "You said you're not sure what's next. The fact that you took the time to answer 100+ questions tells us you're not ACTUALLY lost — you're trying to be honest with yourself about something. Your report names what your answers are pointing toward, even when you couldn't put words to it yourself.",
      cta: 'See what your answers point toward',
    };
  }
  if (variables.testTakingPurpose === 'careerSwitch') {
    return {
      headline: (name) => `${name}, the right pivot starts with the right diagnosis.`,
      body: "You said you're considering a career switch. The mistake most people make is jumping to the next title before naming what's actually broken about the current one. Your report names BOTH — what to leave behind, and what specifically to walk toward — with salary tiers attached.",
      cta: 'See your switch playbook',
    };
  }
  if (variables.testTakingPurpose === 'advance') {
    return {
      headline: (name) => `${name}, the next level isn't more of the same.`,
      body: "You said you're aiming for advancement. The leap to the next title rarely happens by doing more of what you're already doing — it happens by deliberately closing 1-2 specific gaps. Your report names which gaps to close in YOUR case, and the order to close them in.",
      cta: 'See your advancement plan',
    };
  }
  return null;
}

